import { Box, Button, LoadingOverlay, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@tanstack/react-form";

import { orderInfoSchema, type OrderInfo } from "@jasper/client-models";
import { OrderInfoForm, validateSchema } from "@jasper/forms";
import { useCreateDraftOrder, useGetDentists } from "@jasper/use-cases";
import { UserType } from "/@/generated/graphql";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { IconSend } from "@tabler/icons-react";

export const CreateOrderFromOrderInfoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { state } = useLocation();

  const [dentistSearch, setDentistSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(dentistSearch, 300);

  const { dentists, loading: dentistsLoading } = useGetDentists({
    search: debouncedSearch,
  });

  const dentistOptions = useMemo(() => {
    const options = dentists.map(d => ({
      value: d.id,
      label: `Dr ${d.firstName ?? ""} ${d.lastName ?? ""}${
        d.userGroup?.name ? ` - ${d.userGroup.name}` : ""
      }`,
      name: `${d.firstName ?? ""} ${d.lastName ?? ""}${
        d.userGroup?.name ? ` - ${d.userGroup.name}` : ""
      }`,
    }));

    if (
      user?.type === UserType.Dentist &&
      !options.some(o => o.value === user.id)
    ) {
      options.unshift({
        value: user.id,
        label: `Dr. ${user.firstName ?? ""} ${user.lastName ?? ""}`,
        name: `${user.firstName ?? ""} ${user.lastName ?? ""}`,
      });
    }

    return options;
  }, [dentists, user]);

  const handleDentistSearchChange = (value: string) => {
    const isSelectedOptionLabel = dentistOptions.some(o => o.label === value);
    if (!isSelectedOptionLabel) {
      setDentistSearch(value);
    }
  };

  const { createDraftOrder, loading } = useCreateDraftOrder();

  const form = useForm({
    defaultValues: {
      orderInfo: {
        dentistId: user?.type === UserType.Dentist ? user.id : "",
        patient: {
          firstName: "",
          lastName: "",
          birthDate: undefined,
          sex: undefined,
        },
      },
    } as { orderInfo: OrderInfo },
    onSubmit: async ({ value }) => {
      const { orderInfo } = value;
      try {
        const { data } = await createDraftOrder({
          variables: {
            order: { dentistId: orderInfo.dentistId },
            patient: orderInfo.patient,
          },
        });

        if (data?.createDraftOrder) {
          navigate(
            `/orders/${data.createDraftOrder.id}/configure/products/add`,
            { state }
          );
        }
      } catch (error) {
        notifications.show({
          title: "Erreur lors de la création",
          message: error instanceof Error ? error.message : "Erreur inconnue",
          color: "red",
        });
      }
    },
  });

  return (
    <Box pos="relative" p="md" maw={700} mx="auto">
      <LoadingOverlay visible={loading} />
      <Title variant="display" size="lg" pb="s6" pt="s12">
        Pour qui cette commande ?
      </Title>

      <form.Field
        name="orderInfo"
        validators={{
          onSubmit: ({ value }) => {
            const result = validateSchema(orderInfoSchema, value);
            if (result.success === true) {
              return undefined;
            }

            return result.errors;
          },
        }}
      >
        {field => {
          const onSubmitErrors = field.state.meta.errorMap.onSubmit;

          return (
            <OrderInfoForm
              value={field.state.value}
              errors={
                typeof onSubmitErrors === "object" ? onSubmitErrors : undefined
              }
              onChange={next => field.setValue(next as OrderInfo)}
              dentistOptions={dentistOptions}
              onDentistSearchChange={handleDentistSearchChange}
              isDentistsLoading={dentistsLoading}
            />
          );
        }}
      </form.Field>

      <Box mt="md" style={{ textAlign: "right" }}>
        <Button
          rightSection={<IconSend />}
          loading={loading}
          onClick={form.handleSubmit}
        >
          Continuer
        </Button>
      </Box>
    </Box>
  );
};
